import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { motion, AnimatePresence } from 'framer-motion';
import ZazuLogo from '@/components/ZazuLogo';
import AnnouncementBanner from '@/components/AnnouncementBanner';
import LanguageSelector from '@/components/LanguageSelector';
import SocialFooterLinks from '@/components/SocialFooterLinks';
import SEO from '@/components/SEO';
import { Sparkles } from 'lucide-react';
import { 
  BookOpen, Calculator, FlaskConical, Pencil, Star, Trophy,
  Mic, Volume2, Users, Shield, Globe, ArrowRight, Check,
  Heart, Church, Building, ChevronLeft, ChevronRight, Home,
  Menu, X, Quote, GraduationCap, BookMarked, ShieldCheck, Award
} from 'lucide-react';

const LandingPage = () => {
  const { isAuthenticated } = useAuth();
  const { t } = useLanguage();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [demoScreen, setDemoScreen] = useState(0);

  // Hero slideshow images - diverse children learning
  const heroImages = [
    {
      url: 'https://images.pexels.com/photos/8617987/pexels-photo-8617987.jpeg?auto=compress&cs=tinysrgb&w=800',
      alt: 'Engaged diverse students in classroom raising hands with enthusiasm'
    },
    {
      url: 'https://images.pexels.com/photos/8364065/pexels-photo-8364065.jpeg?auto=compress&cs=tinysrgb&w=800',
      alt: 'Kindergarten children gathered around teacher in vibrant classroom'
    },
    {
      url: 'https://images.unsplash.com/photo-1770096679844-57ca92c2b64b?crop=entropy&cs=srgb&fm=jpg&w=800&q=85',
      alt: 'Children drawing with teacher at a table'
    },
    {
      url: 'https://images.unsplash.com/photo-1771765767087-ce71e4a7916a?crop=entropy&cs=srgb&fm=jpg&w=800&q=85',
      alt: 'Teacher showing a child numbers with her fingers'
    }
  ];

  // Auto-advance slideshow
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroImages.length);
    }, 4000);
    return () => clearInterval(timer);
  }, [heroImages.length]);

  // Auto-advance demo walkthrough
  useEffect(() => {
    const demoTimer = setInterval(() => {
      setDemoScreen((prev) => (prev + 1) % 4);
    }, 5000);
    return () => clearInterval(demoTimer);
  }, []);

  const nextSlide = () => setCurrentSlide((prev) => (prev + 1) % heroImages.length);
  const prevSlide = () => setCurrentSlide((prev) => (prev - 1 + heroImages.length) % heroImages.length);

  const features = [
    { icon: BookOpen, title: t('landing.feature_alphabets'), description: t('landing.feature_alphabets_desc'), color: '#0D9488' },
    { icon: Calculator, title: t('landing.feature_math'), description: t('landing.feature_math_desc'), color: '#F59E0B' },
    { icon: FlaskConical, title: t('landing.feature_science'), description: t('landing.feature_science_desc'), color: '#8B5CF6' },
    { icon: Pencil, title: t('landing.feature_writing'), description: t('landing.feature_writing_desc'), color: '#06B6D4' },
    { icon: Heart, title: t('landing.feature_christian'), description: t('landing.feature_christian_desc'), color: '#EC4899' },
    { icon: Mic, title: t('landing.feature_voice'), description: t('landing.feature_voice_desc'), color: '#EF4444' },
  ];

  const ageGroups = [
    { age: '3-4 years', name: 'Pre-K', desc: 'Letter recognition & counting', color: '#0D9488' },
    { age: '4-5 years', name: 'Preschool', desc: 'Phonics foundations & shapes', color: '#F59E0B' },
    { age: '5-6 years', name: 'Kindergarten', desc: 'Early reading & number sense', color: '#8B5CF6' },
    { age: '6-7 years', name: 'Grade 1', desc: 'Fluent reading & basic math', color: '#EC4899' },
    { age: '7-8 years', name: 'Grade 2', desc: 'Comprehension & problem solving', color: '#06B6D4' },
    { age: '8-10 years', name: 'Grade 3-4', desc: 'Advanced concepts & critical thinking', color: '#EF4444' },
  ];

  const testimonials = [
    {
      quote: "My daughter went from struggling with sight words to reading full sentences in just 3 months. Zazu made learning feel like play for her.",
      name: "Amara K.",
      role: "Mom of a 5-year-old",
      initials: "AK"
    },
    {
      quote: "As a Sunday school teacher, the 52-week Bible curriculum is a game-changer. Kids actually look forward to memorizing scripture now!",
      name: "Pastor David L.",
      role: "Children's Ministry Director",
      initials: "DL"
    },
    {
      quote: "The adaptive difficulty keeps my son challenged without frustrating him. He's earned 47 badges and counting!",
      name: "Sarah M.",
      role: "Homeschool Parent",
      initials: "SM"
    },
    {
      quote: "We use Zazu across all our pre-K classrooms. The progress tracking helps us identify where each child needs extra support.",
      name: "Dr. Priya N.",
      role: "Elementary School Principal",
      initials: "PN"
    }
  ];

  const curriculumStandards = [
    { name: 'Common Core ELA', desc: 'Reading, writing, phonics & language arts standards', icon: BookOpen },
    { name: 'Common Core Math', desc: 'Number sense, operations & geometry alignment', icon: Calculator },
    { name: 'NGSS Science', desc: 'Next Generation Science Standards exploration', icon: FlaskConical },
    { name: 'Bible Literacy', desc: '52-week scripture-based faith curriculum', icon: BookMarked },
  ];

  const accessibilityFeatures = [
    { icon: Shield, title: 'Disability Support', desc: 'Customizable for various learning needs' },
    { icon: Globe, title: 'ESL Friendly', desc: 'Support for English language learners' },
    { icon: Users, title: 'Inclusive Design', desc: 'Large buttons, voice control, high contrast' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-teal-50/30">
      <SEO
        title="Zazu — Faith-Based Learning App for Kids Ages 3-10 | Phonics, Math, Bible"
        description="The learning app where kids master phonics, math, and scripture. Faith-based, gamified curriculum for ages 3-10. Bible stories, phonics, math, science. Free forever."
        path="/"
        schema={{
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": "Zazu",
          "url": "https://www.zazued.com",
          "logo": "https://www.zazued.com/logo512.png",
          "description": "Faith-based, gamified learning app for children ages 3-10. Phonics, math, science, and Bible curriculum in one COPPA-compliant platform.",
          "sameAs": []
        }}
      />
      {/* Announcement Banner - Above Header (fixed at top) */}
      <div className="fixed top-0 left-0 right-0 z-[60]">
        <AnnouncementBanner placement="above_header" />
      </div>
      
      {/* Header - adjusts based on banner */}
      <header className="fixed top-0 left-0 right-0 z-50 glass border-b border-white/40 shadow-[0_1px_0_rgba(13,148,136,0.06)]" style={{ top: 'var(--announcement-height, 0)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" data-testid="logo-link">
              <ZazuLogo size="md" />
            </Link>
            
            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-6">
              <a href="#features" className="text-slate-600 hover:text-teal-600 transition">{t('nav.features')}</a>
              <a href="#ages" className="text-slate-600 hover:text-teal-600 transition">{t('nav.age_groups')}</a>
              <Link to="/christian" className="text-purple-600 hover:text-purple-700 transition flex items-center gap-1">
                <span className="text-sm">✝️</span> {t('christian.title')}
              </Link>
              <Link to="/pricing" className="text-slate-600 hover:text-teal-600 transition">{t('nav.pricing')}</Link>
            </nav>
            
            {/* Desktop Auth Buttons */}
            <div className="hidden md:flex items-center gap-3">
              <LanguageSelector variant="compact" className="border-teal-200" />
              {isAuthenticated ? (
                <Link to="/dashboard">
                  <Button data-testid="go-to-dashboard-btn" className="bg-teal-700 hover:bg-teal-800">
                    {t('nav.go_to_dashboard')}
                  </Button>
                </Link>
              ) : (
                <>
                  <Link to="/login">
                    <Button data-testid="login-btn" variant="ghost">{t('nav.login')}</Button>
                  </Link>
                  <Link to="/register">
                    <Button data-testid="get-started-btn" className="bg-teal-700 hover:bg-teal-800">
                      {t('nav.get_started_free')}
                    </Button>
                  </Link>
                </>
              )}
            </div>
            
            {/* Mobile Menu Button */}
            <button 
              className="md:hidden p-2 rounded-lg hover:bg-teal-50 transition"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
              aria-label="Toggle mobile menu"
            >
              {mobileMenuOpen ? <X className="w-6 h-6 text-slate-700" /> : <Menu className="w-6 h-6 text-slate-700" />}
            </button>
          </div>
        </div>
        
        {/* Mobile Menu Dropdown */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden bg-white border-t border-teal-100 overflow-hidden"
            >
              <div className="px-4 py-4 space-y-3">
                <a 
                  href="#features" 
                  className="block py-2 px-3 rounded-lg text-slate-600 hover:bg-slate-100 hover:text-teal-700 transition"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {t('nav.features')}
                </a>
                <a 
                  href="#ages" 
                  className="block py-2 px-3 rounded-lg text-slate-600 hover:bg-slate-100 hover:text-teal-700 transition"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {t('nav.age_groups')}
                </a>
                <Link 
                  to="/christian" 
                  className="block py-2 px-3 rounded-lg text-purple-600 hover:bg-purple-50 transition flex items-center gap-2"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <span>✝️</span> {t('christian.title')}
                </Link>
                <Link 
                  to="/pricing" 
                  className="block py-2 px-3 rounded-lg text-slate-600 hover:bg-slate-100 hover:text-teal-700 transition"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {t('nav.pricing')}
                </Link>
                
                <div className="pt-3 border-t border-slate-100 space-y-2">
                  <div className="flex items-center justify-center pb-2">
                    <LanguageSelector variant="default" className="border-teal-200 w-full" />
                  </div>
                  {isAuthenticated ? (
                    <Link to="/dashboard" onClick={() => setMobileMenuOpen(false)}>
                      <Button className="w-full bg-teal-700 hover:bg-teal-800" data-testid="mobile-dashboard-btn">
                        {t('nav.go_to_dashboard')}
                      </Button>
                    </Link>
                  ) : (
                    <>
                      <Link to="/login" onClick={() => setMobileMenuOpen(false)}>
                        <Button variant="outline" className="w-full" data-testid="mobile-login-btn">
                          {t('nav.login')}
                        </Button>
                      </Link>
                      <Link to="/register" onClick={() => setMobileMenuOpen(false)}>
                        <Button className="w-full bg-teal-700 hover:bg-teal-800" data-testid="mobile-register-btn">
                          {t('nav.get_started_free')}
                        </Button>
                      </Link>
                    </>
                  )}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>
      
      {/* Announcement Banner - Below Header */}
      <div className="pt-16">
        <AnnouncementBanner placement="below_header" />
      </div>

      {/* Hero Section */}
      <section className="relative pt-24 pb-16 px-4 overflow-hidden grain">
        {/* Floating soft-teal blobs for depth */}
        <div aria-hidden className="blob blob-a bg-teal-300/60" style={{ width: 420, height: 420, top: -80, left: -120 }} />
        <div aria-hidden className="blob blob-b bg-cyan-200/60" style={{ width: 360, height: 360, top: 60, right: -100 }} />
        <div aria-hidden className="blob blob-a bg-amber-200/50" style={{ width: 260, height: 260, bottom: -80, left: '35%' }} />

        <div className="relative max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-12 gap-8 items-center">
            <motion.div
              className="lg:col-span-7 lg:-ml-2"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-flex items-center gap-2 glass rounded-full px-4 py-1.5 mb-5 shadow-sm reveal reveal-1">
                <GraduationCap className="w-4 h-4 text-teal-700" />
                <span className="text-sm font-semibold text-teal-800">Ages 3-10</span>
                <span className="text-xs text-teal-500">•</span>
                <span className="text-sm text-teal-700">AI-Powered Learning</span>
              </div>
              <h1 className="font-fredoka text-4xl sm:text-5xl lg:text-[4.25rem] font-bold text-slate-900 leading-[1.05] tracking-tight mb-5 reveal reveal-2" data-testid="hero-h1">
                Where kids master{' '}
                <span className="relative inline-block">
                  <span className="relative z-10 bg-gradient-to-br from-teal-600 via-teal-500 to-cyan-500 bg-clip-text text-transparent">phonics, math</span>
                  <span aria-hidden className="absolute inset-x-0 -bottom-1 h-3 bg-teal-200/70 rounded-full blur-sm" />
                </span>{' '}
                and{' '}
                <span className="bg-gradient-to-br from-amber-500 to-orange-500 bg-clip-text text-transparent">scripture</span>.
              </h1>
              <p className="text-lg font-medium text-teal-700 italic mb-3 reveal reveal-3">
                Impacting lives, one syllable at a time.
              </p>
              <p className="text-base text-slate-600 mb-8 max-w-lg reveal reveal-3">
                Faith-based, gamified curriculum for children ages 3 to 10. Bible stories, phonics, math, and science in one COPPA-compliant platform. Free forever, no credit card required.
              </p>
              <div className="flex flex-wrap gap-4 reveal reveal-4">
                <Link to="/register">
                  <Button
                    data-testid="hero-start-btn"
                    size="lg"
                    className="h-14 px-8 text-lg bg-teal-700 hover:bg-teal-800 rounded-full shadow-lg shadow-teal-700/25 hover:shadow-teal-700/40 hover:-translate-y-0.5 transition-all"
                  >
                    {t('landing.start_learning_free')} <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
                <Link to="/pricing">
                  <Button
                    data-testid="hero-pricing-btn"
                    size="lg"
                    variant="outline"
                    className="h-14 px-8 text-lg rounded-full border-slate-300 bg-white/70 backdrop-blur hover:bg-white"
                  >
                    {t('landing.view_plans')}
                  </Button>
                </Link>
              </div>
              <div className="flex items-center gap-3 mt-8 reveal reveal-4" data-testid="hero-social-proof">
                <ShieldCheck className="w-5 h-5 text-teal-600 flex-shrink-0" />
                <p className="text-sm text-slate-600">
                  <span className="font-bold text-slate-900">COPPA-compliant</span> · Used by homeschool families and Sunday schools across the US.
                </p>
              </div>
            </motion.div>

            {/* Image Slideshow */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="lg:col-span-5 relative"
            >
              {/* Decorative frame - offset ring */}
              <div aria-hidden className="absolute -inset-3 rounded-[2rem] bg-gradient-to-br from-teal-400/30 via-cyan-300/20 to-amber-200/30 blur-xl" />
              <div className="relative rounded-3xl overflow-hidden shadow-2xl h-[320px] bg-slate-100 ring-1 ring-white/60">
                <AnimatePresence mode="wait">
                  <motion.img
                    key={currentSlide}
                    src={heroImages[currentSlide].url}
                    alt={heroImages[currentSlide].alt}
                    loading="lazy"
                    className="w-full h-full object-cover"
                    initial={{ opacity: 0, x: 50 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -50 }}
                    transition={{ duration: 0.5 }}
                  />
                </AnimatePresence>
                
                {/* Slideshow Controls */}
                <div className="absolute inset-0 flex items-center justify-between px-4">
                  <button 
                    onClick={prevSlide}
                    className="w-10 h-10 rounded-full bg-white/80 hover:bg-white flex items-center justify-center shadow-lg transition-all"
                    data-testid="slideshow-prev"
                  >
                    <ChevronLeft className="w-5 h-5 text-slate-700" />
                  </button>
                  <button 
                    onClick={nextSlide}
                    className="w-10 h-10 rounded-full bg-white/80 hover:bg-white flex items-center justify-center shadow-lg transition-all"
                    data-testid="slideshow-next"
                  >
                    <ChevronRight className="w-5 h-5 text-slate-700" />
                  </button>
                </div>
                
                {/* Slideshow Dots */}
                <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-2">
                  {heroImages.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentSlide(index)}
                      className={`w-2 h-2 rounded-full transition-all ${
                        index === currentSlide ? 'bg-white w-6' : 'bg-white/50'
                      }`}
                      data-testid={`slideshow-dot-${index}`}
                    />
                  ))}
                </div>
              </div>
              
              {/* Floating badges */}
              <div className="absolute -top-4 -right-4 bg-white rounded-2xl p-3 shadow-lg">
                <div className="flex items-center gap-2">
                  <Trophy className="w-6 h-6 text-yellow-500" />
                  <div>
                    <p className="font-bold text-sm text-slate-900">Badges</p>
                    <p className="text-xs text-slate-500">Earn rewards!</p>
                  </div>
                </div>
              </div>
              <div className="absolute -bottom-4 -left-4 bg-white rounded-2xl p-3 shadow-lg">
                <div className="flex items-center gap-2">
                  <Star className="w-6 h-6 text-orange-500" />
                  <div>
                    <p className="font-bold text-sm text-slate-900">Level Up</p>
                    <p className="text-xs text-slate-500">Track progress</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* App Preview / Animated Walkthrough Section */}
      <section className="py-16 px-4 bg-white" data-testid="app-preview-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="font-fredoka text-3xl sm:text-4xl font-bold text-slate-900 mb-3">
              See Zazu in Action
            </h2>
            <p className="text-base md:text-lg text-slate-600 max-w-2xl mx-auto">
              Watch how kids explore lessons, earn badges, and grow &mdash; all in one colorful dashboard
            </p>
          </div>
          
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="relative mx-auto max-w-4xl"
          >
            {/* Laptop Frame */}
            <div className="bg-slate-800 rounded-t-2xl pt-4 pb-0 px-4">
              <div className="flex items-center gap-2 mb-3 px-2">
                <div className="w-3 h-3 rounded-full bg-red-400" />
                <div className="w-3 h-3 rounded-full bg-yellow-400" />
                <div className="w-3 h-3 rounded-full bg-green-400" />
                <div className="flex-1 bg-slate-700 rounded-md mx-4 h-6 flex items-center px-3">
                  <AnimatePresence mode="wait">
                    <motion.span 
                      key={demoScreen}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="text-xs text-slate-400"
                    >
                      {['zazued.com/dashboard', 'zazued.com/lesson/phonics', 'zazued.com/bible/week-8', 'zazued.com/badges'][demoScreen]}
                    </motion.span>
                  </AnimatePresence>
                </div>
              </div>
              <div className="bg-gradient-to-br from-teal-50 via-white to-teal-50 rounded-t-lg overflow-hidden" style={{ minHeight: '280px' }}>
                <AnimatePresence mode="wait">
                  {/* Screen 0: Dashboard Overview */}
                  {demoScreen === 0 && (
                    <motion.div
                      key="dashboard"
                      initial={{ opacity: 0, x: 60 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -60 }}
                      transition={{ duration: 0.45 }}
                      className="p-6 space-y-4"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-teal-400 to-teal-500 flex items-center justify-center text-white font-bold text-sm">E</div>
                          <div>
                            <p className="font-bold text-sm text-slate-800">Welcome back, Emma!</p>
                            <p className="text-xs text-slate-500">Level 5 Explorer</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="bg-amber-100 text-amber-700 text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1">
                            <Trophy className="w-3 h-3" /> 247 XP
                          </div>
                          <div className="bg-purple-100 text-purple-700 text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1">
                            <Award className="w-3 h-3" /> 12 Badges
                          </div>
                        </div>
                      </div>
                      <div className="grid grid-cols-3 gap-3">
                        {[
                          { label: 'Phonics', pct: 78, color: 'teal' },
                          { label: 'Math', pct: 62, color: 'amber' },
                          { label: 'Bible', pct: 91, color: 'purple' }
                        ].map(s => (
                          <div key={s.label} className={`bg-white rounded-xl p-3 border border-${s.color}-100 shadow-sm`}>
                            <p className="text-xs text-slate-500 mb-1">{s.label}</p>
                            <div className="w-full bg-slate-100 rounded-full h-2 mb-1">
                              <motion.div 
                                className={`bg-${s.color}-500 h-2 rounded-full`}
                                initial={{ width: 0 }}
                                animate={{ width: `${s.pct}%` }}
                                transition={{ duration: 1.2, delay: 0.3 }}
                              />
                            </div>
                            <p className={`text-xs font-bold text-${s.color}-600`}>{s.pct}%</p>
                          </div>
                        ))}
                      </div>
                      <div className="flex gap-3">
                        <div className="flex-1 bg-gradient-to-r from-teal-500 to-teal-500 rounded-xl p-3 text-white">
                          <p className="text-xs opacity-80">Next Lesson</p>
                          <p className="font-bold text-sm">Letter Sounds: Ch, Sh, Th</p>
                          <p className="text-xs mt-1 opacity-80">10 min &middot; +15 XP</p>
                        </div>
                        <div className="flex-1 bg-gradient-to-r from-purple-500 to-indigo-500 rounded-xl p-3 text-white">
                          <p className="text-xs opacity-80">Bible Week 8</p>
                          <p className="font-bold text-sm">Noah's Ark Adventure</p>
                          <p className="text-xs mt-1 opacity-80">15 min &middot; +25 XP</p>
                        </div>
                      </div>
                    </motion.div>
                  )}

                  {/* Screen 1: Phonics Lesson */}
                  {demoScreen === 1 && (
                    <motion.div
                      key="lesson"
                      initial={{ opacity: 0, x: 60 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -60 }}
                      transition={{ duration: 0.45 }}
                      className="p-6 space-y-4"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-lg bg-teal-100 flex items-center justify-center">
                            <BookOpen className="w-4 h-4 text-teal-600" />
                          </div>
                          <div>
                            <p className="font-bold text-sm text-slate-800">Phonics: Letter Blends</p>
                            <p className="text-xs text-slate-500">Lesson 3 of 8</p>
                          </div>
                        </div>
                        <div className="bg-teal-100 text-teal-700 text-xs font-bold px-3 py-1 rounded-full">+15 XP</div>
                      </div>
                      <div className="bg-white rounded-2xl border border-slate-200 p-5 text-center">
                        <p className="text-sm text-slate-500 mb-2">Which word starts with "Ch"?</p>
                        <p className="text-4xl font-bold text-slate-800 mb-4 font-fredoka">Ch___</p>
                        <div className="grid grid-cols-2 gap-3 max-w-sm mx-auto">
                          {['Chair', 'Shoe', 'Train', 'Phone'].map((w, i) => (
                            <motion.div
                              key={w}
                              initial={{ scale: 0.9, opacity: 0 }}
                              animate={{ scale: 1, opacity: 1 }}
                              transition={{ delay: 0.2 + i * 0.1 }}
                              className={`rounded-xl p-3 text-sm font-bold cursor-pointer transition-all ${
                                i === 0 
                                  ? 'bg-teal-500 text-white shadow-md scale-105' 
                                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                              }`}
                            >
                              {w}
                            </motion.div>
                          ))}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 justify-center">
                        {[1,2,3,4,5,6,7,8].map(i => (
                          <div key={i} className={`w-2 h-2 rounded-full ${i <= 3 ? 'bg-teal-500' : 'bg-slate-200'}`} />
                        ))}
                        <span className="text-xs text-slate-400 ml-2">3/8 complete</span>
                      </div>
                    </motion.div>
                  )}

                  {/* Screen 2: Bible Curriculum */}
                  {demoScreen === 2 && (
                    <motion.div
                      key="bible"
                      initial={{ opacity: 0, x: 60 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -60 }}
                      transition={{ duration: 0.45 }}
                      className="p-6 space-y-4"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center">
                            <BookMarked className="w-4 h-4 text-purple-600" />
                          </div>
                          <div>
                            <p className="font-bold text-sm text-slate-800">Week 8: Noah's Ark</p>
                            <p className="text-xs text-purple-500">Seeds Level (Age 3-5)</p>
                          </div>
                        </div>
                        <div className="bg-amber-100 text-amber-700 text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1">
                          <Star className="w-3 h-3" /> 25 XP
                        </div>
                      </div>
                      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                        <p className="text-xs text-amber-600 font-semibold mb-1">Memory Verse</p>
                        <p className="text-sm text-slate-800 italic">"Two of every kind of animal came to Noah and entered the ark."</p>
                        <p className="text-xs text-amber-500 mt-1">Genesis 7:9</p>
                      </div>
                      <div className="grid grid-cols-3 gap-3">
                        {[
                          { label: 'Story Time', icon: '📖', done: true },
                          { label: 'Memory Verse', icon: '💭', done: true },
                          { label: 'Worksheet', icon: '✏️', done: false }
                        ].map((act, i) => (
                          <motion.div 
                            key={act.label}
                            initial={{ y: 10, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            transition={{ delay: 0.3 + i * 0.15 }}
                            className={`rounded-xl p-3 text-center border ${act.done ? 'bg-teal-50 border-teal-200' : 'bg-white border-slate-200'}`}
                          >
                            <p className="text-xl mb-1">{act.icon}</p>
                            <p className="text-xs font-semibold text-slate-700">{act.label}</p>
                            {act.done && <p className="text-[10px] text-teal-600 font-bold mt-0.5">Done</p>}
                          </motion.div>
                        ))}
                      </div>
                      <div className="flex items-center justify-between bg-purple-50 rounded-xl p-3 border border-purple-100">
                        <div className="flex items-center gap-2">
                          <Volume2 className="w-4 h-4 text-purple-500" />
                          <span className="text-xs text-slate-700 font-semibold">Worship Song: "God's Amazing Creatures"</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <div className="w-1.5 h-3 bg-purple-400 rounded-sm animate-pulse" />
                          <div className="w-1.5 h-5 bg-purple-500 rounded-sm animate-pulse" style={{animationDelay: '0.1s'}} />
                          <div className="w-1.5 h-4 bg-purple-400 rounded-sm animate-pulse" style={{animationDelay: '0.2s'}} />
                          <div className="w-1.5 h-6 bg-purple-500 rounded-sm animate-pulse" style={{animationDelay: '0.3s'}} />
                          <div className="w-1.5 h-3 bg-purple-400 rounded-sm animate-pulse" style={{animationDelay: '0.15s'}} />
                        </div>
                      </div>
                    </motion.div>
                  )}

                  {/* Screen 3: Badges & Achievements */}
                  {demoScreen === 3 && (
                    <motion.div
                      key="badges"
                      initial={{ opacity: 0, x: 60 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -60 }}
                      transition={{ duration: 0.45 }}
                      className="p-6 space-y-4"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-bold text-sm text-slate-800">My Achievements</p>
                          <p className="text-xs text-slate-500">12 badges earned &middot; 247 total XP</p>
                        </div>
                        <div className="bg-gradient-to-r from-amber-400 to-orange-500 text-white text-xs font-bold px-3 py-1 rounded-full">Level 5</div>
                      </div>
                      <div className="grid grid-cols-4 gap-3">
                        {[
                          { icon: '🌟', name: 'First Steps', desc: 'Complete 1st lesson', earned: true },
                          { icon: '📖', name: 'Bookworm', desc: 'Read 10 stories', earned: true },
                          { icon: '🔢', name: 'Math Whiz', desc: 'Ace 5 math quizzes', earned: true },
                          { icon: '✝️', name: 'Bible Scholar', desc: 'Memorize 10 verses', earned: true },
                          { icon: '🏆', name: 'Champion', desc: 'Win 3 multiplayer', earned: true },
                          { icon: '🎵', name: 'Worship Star', desc: '7-day sing-along', earned: true },
                          { icon: '🧩', name: 'Puzzle Pro', desc: 'Solve 20 puzzles', earned: false },
                          { icon: '🚀', name: 'Superstar', desc: 'Reach Level 10', earned: false }
                        ].map((badge, i) => (
                          <motion.div
                            key={badge.name}
                            initial={{ scale: 0, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            transition={{ delay: 0.1 + i * 0.08, type: 'spring', stiffness: 300 }}
                            className={`rounded-xl p-2.5 text-center border transition-all ${
                              badge.earned 
                                ? 'bg-white border-amber-200 shadow-sm' 
                                : 'bg-slate-50 border-slate-200 opacity-50'
                            }`}
                          >
                            <p className="text-2xl mb-1">{badge.icon}</p>
                            <p className="text-[10px] font-bold text-slate-800 leading-tight">{badge.name}</p>
                            <p className="text-[9px] text-slate-500 leading-tight">{badge.desc}</p>
                          </motion.div>
                        ))}
                      </div>
                      <div className="bg-gradient-to-r from-teal-500 to-teal-500 rounded-xl p-3 flex items-center justify-between">
                        <div>
                          <p className="text-xs text-white/80">XP to next level</p>
                          <p className="text-sm font-bold text-white">247 / 300 XP</p>
                        </div>
                        <div className="w-32 bg-white/30 rounded-full h-2.5">
                          <motion.div 
                            className="bg-white h-2.5 rounded-full"
                            initial={{ width: 0 }}
                            animate={{ width: '82%' }}
                            transition={{ duration: 1.5, delay: 0.3 }}
                          />
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
            {/* Laptop Base */}
            <div className="bg-slate-700 h-4 rounded-b-lg mx-auto" style={{width: '110%', marginLeft: '-5%'}} />
            <div className="bg-slate-600 h-2 rounded-b-xl mx-auto" style={{width: '40%'}} />

            {/* Walkthrough Step Indicators */}
            <div className="flex items-center justify-center gap-3 mt-8" data-testid="demo-walkthrough-nav">
              {[
                { label: 'Dashboard', icon: Home },
                { label: 'Lessons', icon: BookOpen },
                { label: 'Bible', icon: BookMarked },
                { label: 'Badges', icon: Trophy }
              ].map((step, i) => (
                <button
                  key={step.label}
                  onClick={() => setDemoScreen(i)}
                  data-testid={`demo-step-${i}`}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    demoScreen === i 
                      ? 'bg-teal-500 text-white shadow-md' 
                      : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                  }`}
                >
                  <step.icon className="w-4 h-4" />
                  <span className="hidden sm:inline">{step.label}</span>
                </button>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-4 bg-gradient-to-br from-teal-50 via-white to-cyan-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-fredoka text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              {t('landing.everything_to_learn')}
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              {t('landing.everything_to_learn_desc')}
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="h-full border-2 border-transparent hover:border-teal-200 transition-all card-hover-lift">
                  <CardContent className="p-6">
                    <div 
                      className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4"
                      style={{ backgroundColor: `${feature.color}15` }}
                    >
                      <feature.icon className="w-7 h-7" style={{ color: feature.color }} />
                    </div>
                    <h3 className="font-bold text-xl text-slate-900 mb-2">{feature.title}</h3>
                    <p className="text-slate-600">{feature.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Christian Education Highlight Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-indigo-950 via-purple-950 to-slate-900 text-white relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-32 h-32 rounded-full border-4 border-white" />
          <div className="absolute bottom-10 right-10 w-48 h-48 rounded-full border-4 border-white" />
          <div className="absolute top-1/2 left-1/3 w-24 h-24 rounded-full border-4 border-white" />
        </div>
        
        <div className="max-w-6xl mx-auto relative">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Content */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <div className="inline-flex items-center gap-2 bg-purple-500/20 border border-purple-500/30 rounded-full px-4 py-2 mb-6">
                <span className="text-2xl">✝️</span>
                <span className="text-sm text-purple-300">Faith-Based Learning</span>
              </div>
              
              <h2 className="text-3xl md:text-4xl font-bold mb-4 leading-tight">
                Christian Education for{' '}
                <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
                  Growing Hearts
                </span>
              </h2>
              
              <p className="text-lg text-slate-300 mb-6">
                A complete 52-week Bible curriculum designed to help children memorize scripture, 
                understand God's love, and build a strong foundation of faith.
              </p>
              
              <ul className="space-y-3 mb-8">
                {[
                  '52-week Bible journey from Genesis to Revelation',
                  'Scripture memory with badges & rewards',
                  'Age-appropriate levels (Seeds, Roots, Warriors)',
                  'Easy picture login for children',
                  'Parent dashboard & progress tracking'
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <Check className="w-5 h-5 text-teal-400 flex-shrink-0" />
                    <span className="text-slate-200">{item}</span>
                  </li>
                ))}
              </ul>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/christian">
                  <Button size="lg" className="bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-lg px-8">
                    Start Free Trial
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
                <Link to="/christian">
                  <Button size="lg" variant="outline" className="border-purple-500/50 text-purple-300 hover:bg-purple-500/20 text-lg px-8">
                    Learn More
                  </Button>
                </Link>
              </div>
              
              <p className="text-sm text-slate-400 mt-4">
                Only <span className="text-purple-400 font-bold">$2.99/month</span> • 7-day free trial
              </p>
            </motion.div>
            
            {/* Preview Cards */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="relative hidden md:block"
            >
              <div className="relative">
                {/* Main Card */}
                <Card className="bg-slate-800/50 border-purple-500/30 backdrop-blur-sm">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-teal-400 to-teal-500 rounded-xl flex items-center justify-center text-2xl">
                        🌱
                      </div>
                      <div>
                        <p className="font-bold text-white">Week 1: Creation</p>
                        <p className="text-sm text-purple-400">Level: Seeds (Age 3-5)</p>
                      </div>
                    </div>
                    <div className="bg-amber-500/20 border border-amber-500/30 rounded-lg p-3 mb-4">
                      <p className="text-xs text-amber-400 mb-1">Memory Verse</p>
                      <p className="text-white text-sm">"In the beginning God created the heavens and the earth." - Genesis 1:1</p>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-slate-400">Activity: Creation drawing</span>
                      <span className="text-xs text-teal-400">+25 XP</span>
                    </div>
                  </CardContent>
                </Card>
                
                {/* Floating Badge Card */}
                <div className="absolute -top-4 -right-4 bg-gradient-to-br from-amber-400 to-orange-500 rounded-2xl p-4 shadow-lg">
                  <div className="text-center">
                    <span className="text-3xl">🏆</span>
                    <p className="text-xs font-bold text-slate-900 mt-1">Champion</p>
                    <p className="text-[10px] text-slate-700">100 verses</p>
                  </div>
                </div>
                
                {/* Stats Card */}
                <div className="absolute -bottom-4 -left-4 bg-slate-800/80 backdrop-blur-sm rounded-xl p-3 border border-purple-500/30">
                  <div className="flex items-center gap-4">
                    <div className="text-center">
                      <p className="text-lg font-bold text-purple-400">52</p>
                      <p className="text-[10px] text-slate-400">Weeks</p>
                    </div>
                    <div className="text-center">
                      <p className="text-lg font-bold text-teal-400">100+</p>
                      <p className="text-[10px] text-slate-400">Verses</p>
                    </div>
                    <div className="text-center">
                      <p className="text-lg font-bold text-amber-400">4</p>
                      <p className="text-[10px] text-slate-400">Badges</p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Testimonials Section — STUB: awaiting real, named, photographed testimonials from first paying families.
          Per site audit (D3.2, D3.5), gradient-circle initials-only cards signal fabricated proof.
          Replaced with an honest early-access invite until real photos + full names + outcome quotes are collected. */}
      <section className="py-20 px-4 bg-slate-50" data-testid="early-families-section">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-amber-100 border border-amber-200 rounded-full px-4 py-1.5 mb-4">
            <Sparkles className="w-4 h-4 text-amber-600" />
            <span className="text-sm font-semibold text-amber-700">Early Access</span>
          </div>
          <h2 className="font-fredoka text-3xl sm:text-4xl font-bold text-slate-900 mb-3">
            Built with our first homeschool families
          </h2>
          <p className="text-base md:text-lg text-slate-600 max-w-2xl mx-auto mb-6">
            We're collecting the first outcome stories from real families using Zazu. Named testimonials with photos and progress details will appear here as they come in — no gradient circles, no invented quotes.
          </p>
          <p className="text-sm text-slate-500">
            Using Zazu with your kids? <Link to="/help" className="text-teal-600 hover:underline font-medium">Share your story →</Link>
          </p>
        </div>
      </section>

      {/* Age Groups Section */}
      <section id="ages" className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-fredoka text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Tailored for Ages 3-10
            </h2>
            <p className="text-base md:text-lg text-slate-600 max-w-2xl mx-auto">
              Curriculum automatically adapts based on your child's age and progress
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {ageGroups.map((group, index) => (
              <motion.div
                key={group.name}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
                viewport={{ once: true }}
                className="bg-white rounded-2xl p-6 border-2 border-slate-100 hover:border-teal-300 transition-all"
              >
                <div className="w-3 h-3 rounded-full mb-3" style={{ backgroundColor: group.color }} />
                <p className="font-bold text-sm mb-1" style={{ color: group.color }}>{group.age}</p>
                <h3 className="font-bold text-xl text-slate-900 mb-1">{group.name}</h3>
                <p className="text-slate-500 text-sm">{group.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Accessibility Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-teal-500 to-teal-600 text-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-fredoka text-3xl sm:text-4xl font-bold mb-4">
              Learning for Everyone
            </h2>
            <p className="text-lg text-teal-100 max-w-2xl mx-auto">
              Built with accessibility in mind for students with disabilities and ESL learners
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            {accessibilityFeatures.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 text-center"
              >
                <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center mx-auto mb-4">
                  <feature.icon className="w-8 h-8" />
                </div>
                <h3 className="font-bold text-xl mb-2">{feature.title}</h3>
                <p className="text-teal-100">{feature.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Organizations Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-amber-50 to-orange-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-fredoka text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
                For Sunday Schools & Organizations
              </h2>
              <p className="text-lg text-slate-600 mb-6">
                Special enterprise plans for churches, schools, and learning centers. 
                Manage students, customize curriculum, and track progress across your organization.
              </p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-teal-600" />
                  <span>Bulk student enrollment</span>
                </li>
                <li className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-teal-600" />
                  <span>Custom curriculum with Bible content</span>
                </li>
                <li className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-teal-600" />
                  <span>Teacher accounts & management</span>
                </li>
                <li className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-teal-600" />
                  <span>Volume pricing from $1.50/student</span>
                </li>
              </ul>
              <div className="flex flex-wrap gap-3">
                <Link to="/register/organization">
                  <Button size="lg" className="h-14 px-8 text-lg bg-purple-600 hover:bg-purple-700 rounded-full" data-testid="org-signup-btn">
                    <Building className="w-5 h-5 mr-2" /> Schools & Churches
                  </Button>
                </Link>
                <Link to="/register/homeschool">
                  <Button size="lg" className="h-14 px-8 text-lg bg-teal-600 hover:bg-teal-700 rounded-full" data-testid="homeschool-signup-btn">
                    <Home className="w-5 h-5 mr-2" /> Homeschool Families
                  </Button>
                </Link>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Card className="p-6 text-center">
                <Church className="w-12 h-12 text-purple-500 mx-auto mb-3" />
                <h3 className="font-bold">Sunday Schools</h3>
                <p className="text-sm text-slate-500">Faith-based learning</p>
              </Card>
              <Card className="p-6 text-center">
                <Building className="w-12 h-12 text-blue-500 mx-auto mb-3" />
                <h3 className="font-bold">Schools</h3>
                <p className="text-sm text-slate-500">Classroom edition</p>
              </Card>
              <Link to="/register/homeschool">
                <Card className="p-6 text-center hover:shadow-lg transition-shadow cursor-pointer border-teal-200 hover:border-teal-400">
                  <Home className="w-12 h-12 text-teal-500 mx-auto mb-3" />
                  <h3 className="font-bold">Homeschool</h3>
                  <p className="text-sm text-slate-500">Family-focused learning</p>
                </Card>
              </Link>
              <Card className="p-6 text-center">
                <Heart className="w-12 h-12 text-pink-500 mx-auto mb-3" />
                <h3 className="font-bold">Daycares</h3>
                <p className="text-sm text-slate-500">Early learning</p>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Curriculum Alignment Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-slate-50 to-teal-50" data-testid="curriculum-alignment-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-2 bg-blue-100 border border-blue-200 rounded-full px-4 py-1.5 mb-4">
              <GraduationCap className="w-4 h-4 text-blue-600" />
              <span className="text-sm font-semibold text-blue-700">Standards Aligned</span>
            </div>
            <h2 className="font-fredoka text-3xl sm:text-4xl font-bold text-slate-900 mb-3">
              Curriculum You Can Trust
            </h2>
            <p className="text-base md:text-lg text-slate-600 max-w-2xl mx-auto">
              Our content aligns with nationally recognized education standards so your child stays on track
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {curriculumStandards.map((standard, index) => (
              <motion.div
                key={standard.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-2xl p-6 border border-slate-200 hover:border-blue-300 hover:shadow-md transition-all text-center"
              >
                <div className="w-14 h-14 rounded-2xl bg-blue-50 flex items-center justify-center mx-auto mb-4">
                  <standard.icon className="w-7 h-7 text-blue-600" />
                </div>
                <h3 className="font-bold text-slate-900 mb-1">{standard.name}</h3>
                <p className="text-sm text-slate-500">{standard.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-fredoka text-3xl sm:text-4xl font-bold text-slate-900 mb-6">
            Ready to Start the Adventure?
          </h2>
          <p className="text-lg text-slate-600 mb-8">
            Join thousands of families using Zazu to make education fun and engaging.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register">
              <Button 
                data-testid="cta-start-btn"
                size="lg" 
                className="h-14 px-10 text-lg bg-teal-700 hover:bg-teal-800 rounded-full"
              >
                Start Free Trial
              </Button>
            </Link>
          </div>
          <div className="flex flex-wrap justify-center gap-6 mt-8 text-sm text-slate-500">
            <span className="flex items-center gap-2"><Check className="w-4 h-4 text-teal-600" /> No credit card required</span>
            <span className="flex items-center gap-2"><Check className="w-4 h-4 text-teal-600" /> Free forever plan</span>
            <span className="flex items-center gap-2"><Check className="w-4 h-4 text-teal-600" /> Cancel anytime</span>
          </div>
          <div className="mt-4 inline-flex items-center gap-2 bg-teal-50 border border-teal-200 rounded-full px-5 py-2 text-sm text-teal-800" data-testid="money-back-guarantee">
            <ShieldCheck className="w-4 h-4 text-teal-600" />
            <span><strong>30-day money-back guarantee.</strong> Not happy? Get a full refund, no questions asked.</span>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400 py-12 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-8 mb-8">
            <div>
              <div className="mb-4">
                <ZazuLogo size="md" className="[&_span]:text-white" />
              </div>
              <p className="text-sm mb-4">Impacting lives, one syllable at a time. AI-powered learning for children ages 3-10.</p>
              <SocialFooterLinks />
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Product</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/features" className="hover:text-white transition-colors" data-testid="footer-features-link">Features</Link></li>
                <li><Link to="/pricing" className="hover:text-white transition-colors">Pricing</Link></li>
                <li><Link to="/customers" className="hover:text-white transition-colors" data-testid="footer-customers-link">Customer stories</Link></li>
                <li><Link to="/about" className="hover:text-white transition-colors" data-testid="footer-about-link">About</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Compare</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/vs/abcmouse" className="hover:text-white transition-colors" data-testid="footer-vs-abcmouse">Zazu vs ABCmouse</Link></li>
                <li><Link to="/vs/homer" className="hover:text-white transition-colors" data-testid="footer-vs-homer">Zazu vs Homer</Link></li>
                <li><Link to="/vs/khan-academy-kids" className="hover:text-white transition-colors" data-testid="footer-vs-khan">Zazu vs Khan Kids</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Support</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/help" className="hover:text-white transition-colors" data-testid="footer-help-link">Help Center</Link></li>
                <li><Link to="/help" className="hover:text-white transition-colors">Contact Us</Link></li>
                <li><Link to="/careers" className="hover:text-white transition-colors" data-testid="footer-careers-link">Careers</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Organizations</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/organization/register" className="hover:text-white transition-colors">For Schools</Link></li>
                <li><Link to="/organization/register" className="hover:text-white transition-colors">For Churches</Link></li>
                <li><Link to="/organization/register" className="hover:text-white transition-colors">Enterprise</Link></li>
              </ul>
            </div>
          </div>
          
          {/* COPPA Compliance Banner */}
          <div className="bg-slate-800/60 rounded-xl p-4 mb-6 border border-slate-700" data-testid="coppa-compliance-banner">
            <div className="flex items-start gap-3">
              <ShieldCheck className="w-5 h-5 text-teal-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-slate-300 mb-1">COPPA Compliant & Child Safe</p>
                <p className="text-xs text-slate-500 leading-relaxed">
                  Zazu is fully compliant with the Children's Online Privacy Protection Act (COPPA). We never collect personal data from children under 13 without verified parental consent. All data is encrypted, never sold to third parties, and parents retain full control over their child's account and data.
                </p>
              </div>
            </div>
          </div>
          
          <div className="border-t border-slate-800 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm">&copy; {new Date().getFullYear()} Zazu. All rights reserved.</p>
            <div className="flex gap-6 text-sm">
              <a href="#" className="hover:text-white transition-colors">Privacy Policy</a>
              <a href="#" className="hover:text-white transition-colors">Terms of Service</a>
              <a href="#" className="hover:text-white transition-colors">COPPA Policy</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
